import { useMachine } from '@xstate/react';
import { Stack } from '@hotjar/hotsauce';
import { useLocation } from 'react-use';
import { useTranslation } from '@hotjar/translations';
import { PageSkeleton } from '../components';
import { invoicesPageMachine } from './invoices.machine';
import { InvoiceDetailsModal } from './components/invoice-details-modal/InvoiceDetailsModal';
import { InvoiceListPlaceholder } from './components/InvoiceListPlaceholder';
import { InvoiceList } from './components/invoice-list/InvoiceList';
import { INVOICES_PAGE, PAGE, trackClick } from '../utils/tracking';
import { InvoiceNotifications } from './components/InvoiceNotifications';
import { InvoiceDetailsInfo } from './components/InvoiceDetailsInfo';
import { SUBSCRIPTIONS } from '../constants/routes';

export function InvoicesPage(): JSX.Element {
  const { t } = useTranslation('checkout', { keyPrefix: 'invoices' });
  const { t: tPageGeneric } = useTranslation('checkout', { keyPrefix: 'page-generic' });
  const location = useLocation();
  const [state, send, service] = useMachine(invoicesPageMachine, {
    devTools: process.env.NODE_ENV === 'development',
    context: { startInInvoiceEditMode: location.search?.includes('edit=true') },
  });
  const { accountId, userId, customer, currency, invoices } = state.context;

  const hasCanceledSubscription = customer?.subscription.status === 'CANCELLED';

  const trackInvoicesClick = (eventName: string) =>
    trackClick({
      eventName,
      uiPosition: PAGE.invoices,
      accountId,
    });

  return (
    <PageSkeleton
      page="invoices"
      isLoading={state.matches('details.initialise') || state.matches('invoices.initialise')}
      isBlocked={state.matches('details.noInvoiceDetails') && state.matches('invoices.noInvoices')}
      blockedViewProps={{
        title: t('no-details-title'),
        description: t('no-details-description'),
        button: {
          text: tPageGeneric('upgrade-plans'),
          navigateTo: SUBSCRIPTIONS,
          isExternal: false,
          onClick: () => trackInvoicesClick(INVOICES_PAGE.upgradePlans),
        },
      }}
    >
      <Stack spacing="x5" direction="column">
        {state.matches('details.viewInvoiceDetails') && (
          <>
            {!hasCanceledSubscription && customer && (
              <Stack spacing="x3" direction="row">
                <InvoiceNotifications
                  accountId={accountId}
                  userId={userId}
                  onUpdate={(emails) => send('UPDATE_BILLING_NOTIFICATIONS', { emails })}
                />
                <InvoiceDetailsInfo
                  customer={customer}
                  onEdit={() => {
                    trackInvoicesClick(INVOICES_PAGE.edit);
                    send('EDIT_ADDRESS');
                  }}
                />

                <InvoiceDetailsModal
                  customer={customer}
                  accountId={accountId}
                  invoiceMachineService={service}
                  onSubmit={() => trackInvoicesClick(INVOICES_PAGE.save)}
                  onCancel={() => {
                    trackInvoicesClick(INVOICES_PAGE.close);
                    send('CANCEL_ADDRESS_UPDATE');
                  }}
                />
              </Stack>
            )}
          </>
        )}

        {state.matches('invoices.viewInvoices') && (
          <InvoiceList invoices={invoices} currency={currency} />
        )}

        {state.matches('invoices.noInvoices') && (
          <InvoiceListPlaceholder
            ownerEmail={customer?.billingAddress?.emailAddress ?? ''}
            additionalEmails={customer?.additionalEmailAddresses ?? undefined}
          />
        )}
      </Stack>
    </PageSkeleton>
  );
}
